<template>
  <div class="billing-paywall">
    <!-- Soft/Strong баннер (НЕ блокирует): paywall_mode soft → мягкий нудж;
         hard но stage ещё не enforce → предупреждение о скорой блокировке. -->
    <v-banner
      v-if="bannerVisible"
      :color="bannerColor"
      dark
      class="text-left mb-2"
    >
      <div class="d-flex align-center">
        <billing-cat v-if="bannerEnding" variant="hungry" :size="54" :dark="false" class="mr-3 flex-shrink-0" />
        <v-icon v-else large color="white" class="mr-3 flex-shrink-0">{{ bannerIcon }}</v-icon>
        <span><strong>{{ $t(view.title) }}</strong><template v-if="bodyText"> — {{ bodyText }}</template></span>
      </div>
      <template v-slot:actions>
        <v-btn
          v-if="view.cta"
          :color="bannerTier === 'strong' ? 'white' : 'info'"
          :outlined="bannerTier === 'strong'"
          @click="onCta"
        >
          {{ $t(effectiveCta.label) }}
        </v-btn>
        <!-- Закрыть: помним в localStorage подпись состояния (tier|reason|dunning_state) на тенанта —
             новое состояние (другая причина, следующий шаг ладдера, strong) покажет баннер снова. -->
        <v-btn icon color="white" class="billing-paywall__dismiss" :title="$t('custom.close')" :aria-label="$t('custom.close')" @click="onDismiss">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-banner>

    <!-- Hard overlay (БЛОК): универсальный payer_state==3 ИЛИ enforced-hard (FT-070,
         только на stage=enforce). Видимость — computed (ER-04): реагирует на cable-апдейт.
         Дружелюбная карточка с котом-маскотом: серьёзность сохранена (доступ снят), без агрессии. -->
    <v-overlay :value="hardBlock" opacity="0.9">
      <v-card :dark="$vuetify.theme.dark" :light="!$vuetify.theme.dark" class="mx-auto text-center pa-6" rounded="xl" elevation="8" style="max-width: min(440px, calc(100vw - 32px))">
        <billing-cat variant="fainted" :size="180" class="mx-auto mb-2" />
        <div class="text-h6 font-weight-medium mt-2 mb-3">{{ overlayTitle }}</div>
        <div class="text-body-2 mb-5 px-2" style="opacity: 0.75">{{ overlayMessage }}</div>
        <div class="d-flex flex-wrap justify-center" style="gap: 10px">
          <v-btn v-if="!isNative && overlayCta" color="success" @click="onOverlayCta">
            {{ $t(overlayCta.label) }}
          </v-btn>
          <v-btn v-if="isIos && !manualBlock" color="success" :disabled="rubDisabled" @click="$emit('iap-ios')">
            {{ $t("other.add_balance") }}
          </v-btn>
          <v-btn v-if="isAndroid && !manualBlock" color="success" :disabled="rubDisabled" @click="$emit('iap-android')">
            {{ $t("other.add_balance") }}
          </v-btn>
          <v-btn outlined color="secondary" @click="$emit('signout')">
            {{ $t("header.sign_out") }}
          </v-btn>
        </div>
        <div v-if="(isIos || isAndroid) && rubDisabled" class="mt-3 text-caption grey--text">
          {{ $t("other.not_possible_pay_in_rub") }}
        </div>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import { Capacitor } from "@capacitor/core";
import moment from "moment";
import BillingCat from "@/components/icons/BillingCat.vue";
import { bannerDismissKey, bannerSignature, readDismissedSignature, rememberDismissedSignature } from "@/helpers/billingPaywall";

// FT-070 (REQ-01/REQ-02/REQ-03): единый UX-слой paywall над billing_summary (FT-069).
// Баннеры (soft/strong) — не блокируют; overlay (hard) — блок. Истинный блок =
// существующий payer_state==3 ИЛИ billing_summary.enforced (серверный энфорсмент только
// на stage=enforce → CON-01: в shadow/notify enforced=false, поведение не меняется).
// Действия (пополнение/переподвязка/IAP/выход) делегируются наверх событиями — логика
// (transactionStart/startInAppPurchase/signOut) остаётся в App.vue, не дублируется.
// Кот-маскот (BillingCat) задаёт лёгкий, дружелюбный тон вместо сухого error-алерта.
export default {
  name: "BillingPaywall",
  components: { BillingCat },
  data() {
    return {
      // Подпись закрытого баннера из localStorage (реактивная копия: storage сам не реактивен).
      dismissedSignature: null,
    };
  },
  created() {
    this.loadDismissed();
  },
  watch: {
    // Смена тенанта в той же сессии — перечитать память под его ключом.
    "mainCompany.id"() {
      this.loadDismissed();
    },
    // Состояние разрешилось (tier none) — память закрытия сбрасываем: следующий такой же
    // инцидент (новый final_warning, снова малый запас) — новый, его показать обязаны.
    // Подпись null и у hard (оверлей): переход strong → hard — не разрешение, память не трогаем,
    // иначе ослабление обратно в тот же strong показало бы закрытый баннер снова.
    bannerSignature(signature) {
      if (signature === null && this.bannerTier === "none" && this.dismissedSignature) this.forgetDismissed();
    },
  },
  computed: {
    view() {
      return this.$store.getters.getPaywall;
    },
    mainCompany() {
      return this.$store.getters.getMainCompany || {};
    },
    payerState() {
      return this.mainCompany.payer_state;
    },
    enforced() {
      const summary = this.mainCompany.billing_summary;
      return !!(summary && summary.enforced);
    },
    // Истинный блок, НО не на странице оплаты: иначе overlay перекрывает /transactions
    // и пользователь не может ни пополнить баланс, ни подключить автоплатёж (rebill-диалог
    // живёт там). Паттерн paywall: блокируем всё, КРОМЕ маршрута восстановления.
    hardBlock() {
      const onPaymentRoute = this.$route && this.$route.name === "Transactions";
      return (this.payerState === 3 || this.enforced) && !onPaymentRoute;
    },
    bannerTier() {
      return this.view.tier; // none | soft | strong | hard
    },
    // Плоский summary для подписи закрытия (тот же, что у store-геттера getPaywall).
    paywallSummary() {
      const summary = this.mainCompany.billing_summary || {};
      const dunning = summary.dunning_summary || {};
      return {
        paywall_mode: summary.paywall_mode,
        paywall_reason: summary.paywall_reason,
        enforced: summary.enforced,
        dunning_state: dunning.state,
        // Те же поля, что у store-геттера getPaywall: без них подпись считала бы tier иначе,
        // чем видимый баннер (нудж без доступного автоплатежа = none).
        currency: this.mainCompany.currency,
        autopay_available: summary.autopay_available,
        balance_negative: Number(this.mainCompany.balance) < 0,
      };
    },
    bannerSignature() {
      return bannerSignature(this.paywallSummary);
    },
    bannerDismissed() {
      return !!this.bannerSignature && this.dismissedSignature === this.bannerSignature;
    },
    // Баннер не показываем, когда экран уже перекрыт overlay (hardBlock), его закрыли в этом
    // состоянии (память в localStorage, см. helpers/billingPaywall) или пользователь не может
    // выполнить CTA (пополнение/автоплатёж — владелец и админ, как у AnnouncementBar; roleMixin).
    // Блок-оверлей — всем, как раньше.
    bannerVisible() {
      if (this.hardBlock || this.bannerDismissed || !this.isAdminPlus) return false;
      return this.bannerTier === "soft" || this.bannerTier === "strong";
    },
    bannerColor() {
      return this.bannerTier === "strong" ? "error" : "warning";
    },
    // Баннер (НЕ блок): «баланс/триал заканчивается» → голодный кот (variant hungry); прочее
    // (нудж автоплатежа, dunning, MP past_due) → стандартная иконка. Блок-оверлей всегда
    // показывает кота в обмороке (variant fainted, задан в шаблоне).
    bannerEnding() {
      const reason = (this.mainCompany.billing_summary || {}).paywall_reason || null;
      return ["trial_ending", "tariff_policy", "abuser_auto"].includes(reason);
    },
    bannerIcon() {
      return this.bannerTier === "strong" ? "mdi-alert-outline" : "mdi-information-outline";
    },
    // CTA из маппера; для чистого payer_state==3 (billing_summary none/absent → tier none, cta null)
    // дефолтим на пополнение — сохраняем прежнее поведение overlay.
    effectiveCta() {
      if (this.manualBlock) return null; // ручная блокировка: пополнение её не снимает — кнопок оплаты нет
      return this.view.cta || { action: "topup", label: "other.add_balance", to: "/transactions" };
    },
    manualBlock() {
      return (this.mainCompany.billing_summary || {}).paywall_reason === "manual";
    },
    bodyText() {
      if (!this.view.text) return null;
      const params = { ...this.view.textParams };
      if (params.next_retry_at) params.date = moment(params.next_retry_at).format("DD.MM.YYYY HH:mm");
      return this.$t(this.view.text, params);
    },
    // Оверлей по легаси payer_state==3 при tier ≠ hard (например, soft-нудж автоплатежа у
    // заблокированного за минус): тексты/CTA soft-состояния тут не к месту — блок есть блок.
    overlayFromView() {
      return this.bannerTier === "hard";
    },
    overlayTitle() {
      return this.overlayFromView && this.view.title ? this.$t(this.view.title) : this.$t("paywall.hard.title");
    },
    overlayMessage() {
      return (this.overlayFromView && this.bodyText) || this.$t("other.message_no_pay");
    },
    overlayCta() {
      if (this.manualBlock) return null;
      return this.overlayFromView ? this.effectiveCta : { action: "topup", label: "other.add_balance", to: "/transactions" };
    },
    rubDisabled() {
      const plan = this.mainCompany.plan;
      return !!((plan && plan.currency === "RUB") || this.mainCompany.currency === "RUB");
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    isIos() {
      return Capacitor.getPlatform().toLowerCase() === "ios";
    },
    isAndroid() {
      return Capacitor.getPlatform().toLowerCase() === "android";
    },
  },
  methods: {
    storage() {
      try {
        return typeof window !== "undefined" ? window.localStorage : null;
      } catch (e) {
        return null;
      }
    },
    loadDismissed() {
      this.dismissedSignature = readDismissedSignature(this.storage(), bannerDismissKey(this.mainCompany.id));
      // Состояние разрешилось, пока вкладка была закрыта (soft → none за ночь, страница
      // перезагружена): watcher bannerSignature перехода не видел, память осталась бы и спрятала
      // следующий такой же инцидент. Сверяем при загрузке — но только когда billing_summary уже
      // пришёл: до гидратации стора tier всегда none, и стирать память по нему нельзя.
      if (this.dismissedSignature && this.mainCompany.billing_summary && this.bannerTier === "none") {
        this.forgetDismissed();
      }
    },
    forgetDismissed() {
      try {
        const st = this.storage();
        if (st) st.removeItem(bannerDismissKey(this.mainCompany.id));
      } catch (e) {
        // приватный режим — просто сбрасываем реактивную копию
      }
      this.dismissedSignature = null;
    },
    onDismiss() {
      const signature = this.bannerSignature;
      if (!signature) return;
      rememberDismissedSignature(this.storage(), bannerDismissKey(this.mainCompany.id), signature);
      this.dismissedSignature = signature;
    },
    onOverlayCta() {
      if (this.overlayCta) this.emitCta(this.overlayCta.action);
    },
    onCta() {
      if (this.effectiveCta) this.emitCta(this.effectiveCta.action);
    },
    emitCta(action) {
      if (action === "rebind") this.$emit("rebind");
      else if (action === "subscribe") this.$emit("subscribe"); // FT-073: MP → /transactions
      else if (action === "confirm") this.$emit("confirm"); // FT-071: 3DS/подтверждение Stripe — на /transactions, не в TopupDialog
      else this.$emit("topup");
    },
  },
};
</script>
